/**
 * BrandSelector Component
 * 
 * Radio button list for product brands (single selection).
 * Fetches brands from API on mount and displays all as radio buttons.
 * 
 * Requirements: 6.3, 6.4
 */

import { useState, useEffect } from 'react';
import { getProtectedClient } from '../api/client';

export interface Brand {
  id: number;
  name: string;
  slug: string;
}

export interface BrandSelectorProps {
  value: number | null;
  onChange: (brandId: number | null) => void;
  disabled?: boolean;
}

export function BrandSelector({ value, onChange, disabled = false }: BrandSelectorProps) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const client = getProtectedClient();
        const response = await client.get<{ success: boolean; data: Brand[] }>('/brands');
        
        if (response.data.success) {
          // Filter out "Uncategorized"
          const filtered = response.data.data.filter(
            brand => brand.name.toLowerCase() !== 'uncategorized' && brand.slug !== 'uncategorized'
          );
          setBrands(filtered);
        } else {
          setError('Failed to load brands');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load brands');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrands();
  }, []);

  if (isLoading) {
    return (
      <div className="brand-selector brand-selector--loading">
        <span className="loading-text">Loading brands...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brand-selector brand-selector--error">
        <span className="error-text">{error}</span>
      </div>
    );
  }

  return (
    <div className="brand-selector">
      <div className="brand-radio-list">
        {brands.length === 0 ? (
          <span className="brand-selector__no-results">No brands available</span>
        ) : (
          <>
            <label className="brand-radio-item">
              <input
                type="radio"
                name="brand"
                checked={value === null}
                onChange={() => onChange(null)}
                disabled={disabled}
              />
              <span className="brand-name">None</span>
            </label>
            {brands.map(brand => {
              const isSelected = brand.id === value;
              return (
                <label key={brand.id} className="brand-radio-item">
                  <input
                    type="radio"
                    name="brand"
                    checked={isSelected}
                    onChange={() => onChange(brand.id)}
                    disabled={disabled}
                  />
                  <span className="brand-name">{brand.name}</span>
                </label>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

export default BrandSelector;
